import { CartLineItem } from '@adapters/bigcommerce/types/cart';
import {
  Breadcrumbs,
  MecProduct,
  PriceType,
  VariantInventory,
} from '@adapters/bigcommerce/types/product';
import { AlgonomyProduct } from '@components/global/Recommendations/Loader';
import { lowInventoryThreshold } from '@components/product/ProductBuyZone/ProductLowInventory';
import { TrackCartEventItem } from './trackAddToCartEvent';
import getDefaultProductCategory from '@adapters/bigcommerce/operations/utils/getDefaultProductCategory';

export const getGenderFromString = (input?: string): string | null => {
  if (!input || !input.includes('-')) return null;

  const segments = input.split(' - ');
  const result =
    segments.length > 1 && segments[segments.length - 1]?.trim()
      ? segments[segments.length - 1]?.trim()
      : null;
  return result;
};

export const convertPriceStringToNumber = (priceString: string): number => {
  // First, remove currency symbols and spaces
  const cleanedPrice = priceString.replace(/[$\s]/g, '');

  // Check if it's French format (1000,00) or English format (1,000.00)
  const isFrenchFormat = /^\d+,\d{2}$/.test(cleanedPrice);

  if (isFrenchFormat) {
    // French format: replace comma with decimal point
    return Number(cleanedPrice.replace(',', '.'));
  } else {
    // English format: remove thousands separators
    return Number(cleanedPrice.replace(/,/g, ''));
  }
};

export const generateProductTrackingData = (
  product: AlgonomyProduct,
  index: number,
  listName?: string
) => {
  const displayPrice = convertPriceStringToNumber(product.displayPrice);
  const displaySalePrice = convertPriceStringToNumber(product.displaySalePrice);

  return {
    code: product.id,
    gaName: product.displayName,
    hasMemberPriceDifference: false,
    selectedPrice: {
      price: {
        value: Number(displaySalePrice),
      },
      basePrice: {
        value: Number(displayPrice),
      },
    },
    index,
    brand: {
      name: product?.brand,
    },
    category: product.trackingimpressionCategoryPath,
    itemListName: listName ?? null,
    itemColour: product?.variantColour,
    gender: product?.gender === '' ? null : product?.gender,
  };
};

export const createCategoryPath = (breadcrumbs: Breadcrumbs[]) => {
  if (!breadcrumbs) return '';

  // Check if any breadcrumb has undefined name
  if (breadcrumbs.some((breadcrumb) => !breadcrumb?.name)) return null;

  return breadcrumbs.reduce((categoryName, breadcrumb) => {
    const name = breadcrumb?.name?.toLowerCase();
    const transformedName =
      name === 'all' ? 'products' : name === 'tout' ? 'produits' : name;
    return `${categoryName && `${categoryName}/`}${transformedName}`;
  }, '');
};

interface DiscountCalculation {
  discount: number | null;
  discountTier: string | null;
}

export const calculateDiscount = (
  originalPrice: number,
  salePrice: number
): DiscountCalculation => {
  if (
    typeof originalPrice !== 'number' ||
    typeof salePrice !== 'number' ||
    originalPrice === 0 ||
    salePrice === 0
  ) {
    return { discount: null, discountTier: null };
  }

  const priceDiff = originalPrice - salePrice;
  const discount = priceDiff > 0 ? priceDiff : null;
  const discountTier = discount ? (discount / originalPrice) * 100 : null;

  return {
    discount: discount ? Number(discount?.toFixed(2)) : null,
    discountTier: discountTier?.toFixed(0) ?? null,
  };
};

export const productDescriptionComingSoon = (description: string) => {
  const comingSoonPhrases = [
    'Campsite reservations wait for no one.',
    'Nouveauté. La description suivra bientôt.',
  ];
  return comingSoonPhrases.some((phrase) => description?.includes(phrase));
};

export const determineLowInventoryStatus = (
  selectedVariant?:
    | MecProduct['variants'][number]
    | CartLineItem
    | TrackCartEventItem
    | { inventoryAggregated: VariantInventory }
) => {
  const availableToSell = selectedVariant?.inventoryAggregated?.availableToSell;

  return (
    typeof availableToSell === 'number' &&
    availableToSell <= lowInventoryThreshold
  );
};

interface Category {
  node: {
    breadcrumbs: { edges: { node: { name: string } }[] };
  };
}

export function getCategoryString(locale: string, categories: Category[]) {
  const lowestCategory = getDefaultProductCategory(locale, categories);

  if (!lowestCategory) return 'Uncategorized';

  return (
    (lowestCategory.node?.breadcrumbs?.edges ?? []).reduce(
      (acc: string, edge: { node: { name: string } }) =>
        edge?.node?.name
          ? `${acc}${edge.node.name.toLowerCase()}${
              lowestCategory.node?.breadcrumbs?.edges &&
              lowestCategory.node.breadcrumbs.edges.indexOf(edge) <
                lowestCategory.node.breadcrumbs.edges.length - 1
                ? '/'
                : ''
            }`
          : acc,
      ''
    ) ?? 'Uncategorized'
  );
}

export const PromotionPageType = {
  LP: 'LP',
  PLP: 'PLP',
  PDP: 'PDP',
  CategoryIntroPage: 'CIP',
  Homepage: 'Homepage',
  BrandedStore: 'Branded Store',
} as const;

export const determinePageType = () => {
  const isServerSide = typeof window === 'undefined';
  const isHomepage =
    !isServerSide &&
    (window.location.pathname === '/en' || window.location.pathname === '/fr');

  if (isServerSide) {
    return PromotionPageType.PLP; // Default to PLP for server-side
  }
  if (window.location.pathname.includes('/p/')) {
    return PromotionPageType.LP;
  }
  if (window.location.pathname.includes('/category/')) {
    return PromotionPageType.CategoryIntroPage;
  }
  if (isHomepage) {
    return PromotionPageType.Homepage;
  }
  if (window.location.pathname.includes('/branded-store/')) {
    return PromotionPageType.BrandedStore;
  }
  if (window.location.pathname.includes('/product/')) {
    return PromotionPageType.PDP;
  }
  if (
    `${window.location.pathname}/`.includes('/products/') ||
    `${window.location.pathname}/`.includes('/produits/')
  ) {
    return PromotionPageType.PLP;
  }

  return 'unassigned';
};

export const buildCreativeSlotName = ({
  sliceIndex = 0,
  interactionLocation,
  itemIndex,
}: {
  sliceIndex?: number | string;
  interactionLocation: string;
  itemIndex?: number | string;
}) => {
  const pageType = determinePageType();
  return `${pageType}-${sliceIndex}-${interactionLocation}${
    typeof itemIndex !== 'undefined' ? `-${itemIndex}` : ''
  }`;
};

const MARKDOWN_LABEL_MAX_LENGTH = 100;

const stripMarkdown = (text: string): string =>
  text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#+\s*/gm, '')
    .trim()
    .slice(0, MARKDOWN_LABEL_MAX_LENGTH);

export const resolvedBannerToTrackingLabel = (banner: {
  title?: string;
  firstLineMarkdown?: string;
}): string | null => {
  const trimmedTitle = banner.title?.trim();
  const raw =
    trimmedTitle ||
    (banner.firstLineMarkdown ? stripMarkdown(banner.firstLineMarkdown) : '');
  return raw.trim() || null;
};

export const createDealsBannerTrackingText = ({
  priceType,
  t,
  prismicBannerTitles = [],
  memberPriceLabel,
}: {
  priceType: PriceType;
  t: (key: string) => string;
  prismicBannerTitles?: string[];
  memberPriceLabel?: string;
}) => {
  const bannerTexts: string[] = [];

  if (priceType === 'sale') {
    bannerTexts.push(`${t('product.info.banner.deals.title')}`);
  }

  if (priceType === 'clearance') {
    bannerTexts.push(t('product.info.banner.clearance.title'));
  }

  if (memberPriceLabel && priceType !== 'clearance') {
    bannerTexts.push(memberPriceLabel);
  }

  const MAX_PRISMIC_TITLES = 4; // getResolvedDealsBanners caps at 2 per slot × 2 slots
  const MAX_TITLE_LENGTH = 100;
  const sanitized = [
    ...new Set(
      prismicBannerTitles
        .map((s) => s.trim().slice(0, MAX_TITLE_LENGTH))
        .filter(Boolean)
    ),
  ].slice(0, MAX_PRISMIC_TITLES);
  bannerTexts.push(...sanitized);

  return bannerTexts.length ? bannerTexts : null;
};
